import { formatDistanceToNow } from 'date-fns';
import { Check, X, Bell, AlertTriangle, Info } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Notification, useResolveNotification, useDismissNotification } from '../api/useNotifications';

interface NotificationItemProps {
  notification: Notification;
}

const priorityStyles = {
  ACTION: { border: 'border-l-amber-500', bg: 'bg-amber-50', icon: AlertTriangle, iconColor: 'text-amber-600' },
  ALERT: { border: 'border-l-blue-500', bg: 'bg-blue-50', icon: Info, iconColor: 'text-blue-600' },
  SYSTEM: { border: 'border-l-gray-300', bg: 'bg-gray-50', icon: Bell, iconColor: 'text-gray-400' },
};

export function NotificationItem({ notification }: NotificationItemProps) {
  const resolveMutation = useResolveNotification();
  const dismissMutation = useDismissNotification();

  const style = priorityStyles[notification.priority] || priorityStyles.SYSTEM;
  const Icon = style.icon;
  const isResolved = !!notification.resolvedAt;

  return (
    <div
      className={`flex items-start gap-3 p-3 border-l-4 ${style.border} ${style.bg} rounded-r-lg ${
        isResolved ? 'opacity-60' : ''
      }`}
    >
      <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${style.iconColor}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium text-gray-900 ${!notification.isRead ? 'font-semibold' : ''}`}>
          {notification.title}
        </p>
        {notification.body && (
          <p className="text-xs text-gray-600 mt-0.5">{notification.body}</p>
        )}
        <p className="text-xs text-gray-400 mt-1">
          {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
        </p>
      </div>
      {!isResolved && notification.priority === 'ACTION' && (
        <div className="flex gap-1 shrink-0">
          <Button
            size="sm"
            variant="primary"
            onClick={() => resolveMutation.mutate({ id: notification.id })}
            disabled={resolveMutation.isPending}
          >
            <Check className="h-3 w-3" />
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => dismissMutation.mutate({ id: notification.id })}
            disabled={dismissMutation.isPending}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
}
